import React from "react"
import { Button, Tabs } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark } from "@fortawesome/free-solid-svg-icons"
import { isDataTile } from "features/Dashboard/types/dashboardDocument"
import {
  useDashboardStore,
  findTile,
} from "features/Dashboard/runtime/dashboardStore"
import { TYPE_LABEL } from "features/Dashboard/components/Tiles/TileChrome.component"
import DataTab from "./DataTab.component"
import MeasuresTab from "./MeasuresTab.component"
import DisplayTab from "./DisplayTab.component"
import ControlsTab from "./ControlsTab.component"

/**
 * The right rail for the selected tile: Data (what it runs), Measures (the
 * rows it derives), Display (how it draws) and Controls (what viewers may
 * change). Text and image tiles have only Display. The open tab stays put
 * when another tile is selected; the tab contents are keyed by tile so no
 * draft carries over.
 */
const Inspector: React.FC = () => {
  // Two selectors of stable values: a selector returning findTile's fresh
  // { page, tile } object would re-render without end.
  const tile = useDashboardStore((s) =>
    s.selectedTileId ? findTile(s, s.selectedTileId)?.tile : undefined
  )
  const pageId = useDashboardStore((s) =>
    s.selectedTileId ? findTile(s, s.selectedTileId)?.page.id : undefined
  )
  const selectTile = useDashboardStore((s) => s.selectTile)
  if (!tile || !pageId) return null
  const data = isDataTile(tile)
  const typeLabel = TYPE_LABEL[tile.type]
  const items = [
    ...(data
      ? [
          {
            key: "data",
            label: "Data",
            children: <DataTab key={tile.id} tile={tile} pageId={pageId} />,
          },
          {
            key: "measures",
            label: "Measures",
            children: (
              <MeasuresTab key={tile.id} tile={tile} pageId={pageId} />
            ),
          },
        ]
      : []),
    {
      key: "display",
      label: "Display",
      children: <DisplayTab key={tile.id} tile={tile} pageId={pageId} />,
    },
    ...(data
      ? [
          {
            key: "controls",
            label: "Controls",
            children: (
              <ControlsTab key={tile.id} tile={tile} pageId={pageId} />
            ),
          },
        ]
      : []),
  ]
  return (
    <div className="dq-inspector">
      <div className="dq-inspector__header">
        <div className="dq-inspector__heading">
          <span className="dq-inspector__title">{tile.title || typeLabel}</span>
          {tile.title && <span className="dq-inspector__type">{typeLabel}</span>}
        </div>
        <Button
          type="text"
          className="dq-inspector__close"
          aria-label="Close inspector"
          icon={<FontAwesomeIcon icon={faXmark} />}
          onClick={() => selectTile(null)}
        />
      </div>
      <Tabs
        size="small"
        items={items}
        defaultActiveKey={data ? "data" : "display"}
        className="dq-inspector__tabs"
      />
    </div>
  )
}

export default Inspector
